import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { buildListParams, type DataGridQuery, type PageRequest } from "@/hooks/use-data-grid";
import { ApiError, getJson, SessionExpiredError } from "@/lib/api-client";
import { projectKeys, shareKeys } from "@/lib/query";
import {
  type CreateShareRequest,
  photoShareStateResponseSchema,
  type RevokeSharesRequest,
  shareListResponseSchema,
  shareSummaryResponseSchema,
} from "@/schemas/shares";

export async function postJson(url: string, body: unknown): Promise<unknown> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (res.status === 401) throw new SessionExpiredError();
  const parsed: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      typeof parsed === "object" && parsed !== null && "error" in parsed
        ? String(parsed.error)
        : `The server answered ${res.status}`;
    throw new ApiError(message, res.status);
  }
  return parsed;
}

export function useShares(
  projectId: string,
  query: DataGridQuery,
  pageRequest: PageRequest,
  scope: string,
) {
  return useQuery({
    queryKey: shareKeys.list(
      projectId,
      { ...query, scope },
      `${pageRequest.direction}:${pageRequest.cursor}`,
    ),
    queryFn: () => {
      const params = buildListParams(query, pageRequest);
      if (scope) params.set("scope", scope);
      return getJson(
        `/api/projects/${encodeURIComponent(projectId)}/shares?${params}`,
        shareListResponseSchema,
      );
    },
    placeholderData: keepPreviousData,
  });
}

export function useShareSummary(projectId: string, enabled: boolean) {
  return useQuery({
    queryKey: shareKeys.summary(projectId),
    queryFn: async () =>
      (
        await getJson(
          `/api/projects/${encodeURIComponent(projectId)}/share-summary`,
          shareSummaryResponseSchema,
        )
      ).data,
    enabled,
  });
}

export function usePhotoShareState(photoId: string) {
  return useQuery({
    queryKey: shareKeys.photo(photoId),
    queryFn: async () =>
      (
        await getJson(
          `/api/photos/${encodeURIComponent(photoId)}/share-state`,
          photoShareStateResponseSchema,
        )
      ).data,
  });
}

function useShareMutation<Request>(url: string, failure: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: Request) => postJson(url, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: shareKeys.all });
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : failure);
    },
  });
}

export function useCreateShare() {
  return useShareMutation<CreateShareRequest>("/api/shares", "Could not change the share");
}

export function useRevokeShares() {
  return useShareMutation<RevokeSharesRequest>("/api/shares/revoke", "Could not revoke the shares");
}
